import { useState, type FormEvent } from "react";
import { practiceDuration } from "@wmf/shared/practiceMetrics";
import { get, send } from "../api.js";
import { useAsync } from "../useAsync.js";

export const practiceTargetOptions = [15, 30, 45, 60, 90, 120] as const;
type TargetOption = (typeof practiceTargetOptions)[number];
const isTargetOption = (minutes: number): minutes is TargetOption => practiceTargetOptions.includes(minutes as TargetOption);

interface StoredGoal {
  minutesPerWeek: number | null;
  updatedAt: string | null;
}

const localKey = (studentId: string | undefined) => `wmf.practice-target.v1.${studentId ?? "self"}`;

function readLocalGoal(key: string): number | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(key);
    const minutes = stored === null ? NaN : Number(stored);
    return isTargetOption(minutes) ? minutes : null;
  } catch {
    return null;
  }
}

function clearLocalGoal(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // The stored goal is the source of truth now; a browser that will not forget its copy only means the offer reappears.
  }
}

/**
 * The learner's optional weekly practice goal, compared with the saved-practice
 * total the page already shows. With `stored` off it is the browser-local goal
 * exactly as before. With it on, the goal lives on the account, a goal left in
 * this browser is offered for saving rather than moved silently, and nothing
 * here touches leaderboards or awards.
 */
export function PracticeWeeklyGoal({ studentId, stored, totalSeconds }: { studentId?: string; stored: boolean; totalSeconds: number }) {
  const key = localKey(studentId);
  const path = `/practice-goal/family/${encodeURIComponent(studentId ?? "")}`;
  const account = useAsync(() => (stored && studentId ? get<StoredGoal>(path) : Promise.resolve(null)), [stored, studentId, path]);
  const [localMinutes, setLocalMinutes] = useState<number | null>(() => readLocalGoal(key));
  const [changed, setChanged] = useState<number | null | undefined>(undefined);
  const [choice, setChoice] = useState(() => (stored ? "" : (readLocalGoal(key)?.toString() ?? "")));
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  // In stored mode `undefined` means "not known yet": still loading, or the read failed.
  const minutes: number | null | undefined = stored ? (changed !== undefined ? changed : account.data?.minutesPerWeek) : localMinutes;
  const browserGoal = stored && minutes === null ? readLocalGoal(key) : null;

  async function write(method: "PUT" | "DELETE", target: number | null, done: string) {
    setBusy(true);
    setNotice("");
    try {
      const result = await send<StoredGoal>(method, path, method === "PUT" ? { minutesPerWeek: target } : undefined);
      setChanged(result.minutesPerWeek);
      setLocalMinutes(null);
      clearLocalGoal(key);
      setChoice("");
      setNotice(done);
    } catch {
      setNotice(method === "PUT" ? "Your goal could not be saved. Try again." : "Your goal could not be removed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = Number(choice);
    if (!isTargetOption(target)) return;
    if (stored) {
      void write("PUT", target, "Weekly goal saved to your account.");
      return;
    }
    try {
      window.localStorage.setItem(key, String(target));
      setLocalMinutes(target);
      setNotice("Weekly goal saved in this browser.");
    } catch {
      setNotice("Your browser could not save this goal. Check its storage settings and try again.");
    }
  }

  function remove() {
    if (stored) {
      void write("DELETE", null, "Weekly goal removed.");
      return;
    }
    try {
      window.localStorage.removeItem(key);
      setLocalMinutes(null);
      setChoice("");
      setNotice("Weekly goal removed.");
    } catch {
      setNotice("Your browser could not remove this goal. Try again.");
    }
  }

  const loading = stored && minutes === undefined && account.loading;
  const failed = stored && minutes === undefined && !account.loading;
  const goalSeconds = typeof minutes === "number" ? minutes * 60 : 0;

  return (
    <section className="panel metric-goal" aria-labelledby="metric-goal-title">
      <div className="metric-goal-heading">
        <div>
          <h2 id="metric-goal-title">Personal weekly goal</h2>
          <p className="small muted">
            Optional and based on saved practice only. This goal {stored ? "is saved to your account" : "stays in this browser"} and does not affect leaderboards or awards.
          </p>
        </div>
        {typeof minutes === "number" && (
          <button className="quiet small" type="button" disabled={busy} onClick={remove}>
            Remove goal
          </button>
        )}
      </div>
      {loading && <p role="status">Loading your goal…</p>}
      {failed && (
        <p role="alert">
          Your weekly goal could not be loaded.{" "}
          <button className="quiet small" type="button" onClick={account.reload}>
            Retry
          </button>
        </p>
      )}
      {typeof minutes === "number" && (
        <>
          <p>
            You’ve logged <strong className="metric-number">{practiceDuration(totalSeconds)}</strong> toward <strong>{practiceDuration(goalSeconds)}</strong> this week.
          </p>
          <progress
            className="metric-goal-progress"
            value={Math.min(totalSeconds, goalSeconds)}
            max={goalSeconds}
            aria-label={`Weekly practice goal progress: ${practiceDuration(totalSeconds)} of ${practiceDuration(goalSeconds)}`}
          />
          <p className="small muted">{totalSeconds >= goalSeconds ? "Goal reached. Great consistency this week." : "Each saved session moves you toward your goal."}</p>
        </>
      )}
      {browserGoal !== null && (
        <p>
          This browser has a weekly goal of <strong>{practiceDuration(browserGoal * 60)}</strong>.{" "}
          <button type="button" disabled={busy} onClick={() => void write("PUT", browserGoal, "Weekly goal saved to your account.")}>
            Save this goal to your account
          </button>
        </p>
      )}
      {!loading && !failed && (
        <form className="metric-goal-form" onSubmit={submit}>
          <label className="metric-field" htmlFor="metric-goal-minutes">
            <span>{minutes === null ? "Choose a weekly target" : "Change weekly target"}</span>
            <select
              id="metric-goal-minutes"
              value={choice}
              onChange={(event) => {
                setChoice(event.target.value);
                setNotice("");
              }}
            >
              <option value="">Choose a target</option>
              {practiceTargetOptions.map((option) => (
                <option key={option} value={option}>
                  {option} minutes per week
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={!choice || busy}>
            {minutes === null ? "Set goal" : "Save goal"}
          </button>
        </form>
      )}
      {notice && (
        <p className="small" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}
